/**
 * Phase 14 Plan 05 — HELP-05: per-user tour completion tracker.
 *
 * useTourState(tourId) returns the React Query result for the (user_id, tour_id)
 * row in public.user_tour_state, augmented with three mutation helpers:
 *   markSeen()      — status='seen'      (navigate-away mid-tour, D-06)
 *   markDismissed() — status='dismissed' (Esc or Skip, D-06)
 *   markCompleted() — status='completed' (Done on last step, D-06)
 *
 * D-07/D-06 note: D-07 says "writes status='seen' on completion". D-06 supersedes:
 * the 3-state exit means auto-launch writes the status matching the user's actual
 * exit path — Done→completed, Esc/Skip→dismissed, navigate-away→seen. No change
 * to D-07 behavior is needed; only the literal wording is clarified here.
 *
 * Threat mitigations:
 *   T-14-15: RLS USING (user_id = auth.uid()) on SELECT/UPDATE restricts cross-user read.
 *   T-14-16: RLS WITH CHECK (user_id = auth.uid()) on INSERT/UPDATE + explicit
 *             user_id: (await supabase.auth.getUser()).data.user.id in the upsert
 *             prevents cross-user writes.
 *
 * The `user_tour_state` table is added by migration 20260423000003 — pushed in plan 14-06.
 * Until the migration lands in the live DB, queries 404. The hook falls back to a
 * per-user localStorage mirror so tours do not re-nag after Skip/Done/Esc pre-deploy.
 * Once the table exists, the DB is authoritative; localStorage becomes a harmless echo.
 */
import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type TourStatus = 'seen' | 'dismissed' | 'completed';
export type TourStateRow = {
  user_id: string;
  tour_id: string;
  status: TourStatus;
  last_shown_at: string;
};

// Pre-deploy fallback: when user_tour_state table doesn't exist yet, mirror status
// to localStorage keyed per (user, tour) so auto-launch respects Skip/Done across refreshes.
const LS_PREFIX = 'tour-state';
const lsKey = (userId: string, tourId: string) => `${LS_PREFIX}:${userId}:${tourId}`;

function readLsStatus(userId: string, tourId: string): TourStatus | null {
  try {
    const raw = localStorage.getItem(lsKey(userId, tourId));
    return raw === 'seen' || raw === 'dismissed' || raw === 'completed' ? raw : null;
  } catch {
    return null;
  }
}

function writeLsStatus(userId: string, tourId: string, status: TourStatus): void {
  try {
    localStorage.setItem(lsKey(userId, tourId), status);
  } catch {
    // Safari private mode / quota exceeded — non-fatal
  }
}

// Supabase generated types don't yet include user_tour_state (added in migration
// 20260423000003 — genTypes run in 14-06). Cast through unknown to avoid tsc errors.
type SupabaseFrom = {
  from: (t: string) => {
    select: (cols: string) => {
      eq: (col: string, val: string) => {
        eq: (col: string, val: string) => {
          maybeSingle: () => Promise<{ data: TourStateRow | null; error: Error | null }>;
        };
      };
    };
    upsert: (
      row: { user_id: string; tour_id: string; status: TourStatus; last_shown_at: string },
      opts: { onConflict: string }
    ) => Promise<{ error: Error | null }>;
  };
};

export function useTourState(
  tourId: string
): UseQueryResult<TourStateRow | null, Error> & {
  markSeen: () => void;
  markDismissed: () => void;
  markCompleted: () => void;
} {
  const qc = useQueryClient();

  const query = useQuery<TourStateRow | null, Error>({
    queryKey: ['user_tour_state', tourId],
    queryFn: async () => {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData.user;
      if (!user) return null;
      const { data, error } = await (supabase as unknown as SupabaseFrom)
        .from('user_tour_state')
        .select('user_id, tour_id, status, last_shown_at')
        .eq('user_id', user.id)
        .eq('tour_id', tourId)
        .maybeSingle();
      if (error) {
        // Table not deployed yet — fall back to localStorage mirror.
        const cached = readLsStatus(user.id, tourId);
        if (cached) {
          return {
            user_id: user.id,
            tour_id: tourId,
            status: cached,
            last_shown_at: new Date().toISOString(),
          };
        }
        console.warn('[useTourState] query error (table may not exist yet):', error.message);
        return null;
      }
      return data;
    },
    staleTime: 5 * 60 * 1000,  // 5 min — tour state is stable per session
    refetchOnWindowFocus: false,
  });

  const upsert = useMutation<void, Error, TourStatus>({
    mutationFn: async (status: TourStatus) => {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData.user;
      if (!user) throw new Error('[useTourState] not authenticated');
      // Persist locally first — survives table-missing 404 below so Skip/Done
      // are remembered across page refreshes pre-deploy.
      writeLsStatus(user.id, tourId, status);
      // T-14-16: explicitly set user_id from auth — not from props or input.
      const { error } = await (supabase as unknown as SupabaseFrom)
        .from('user_tour_state')
        .upsert(
          {
            user_id: user.id,
            tour_id: tourId,
            status,
            last_shown_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,tour_id' }
        );
      if (error) {
        // Table not deployed yet — localStorage fallback already persisted above.
        console.warn('[useTourState] upsert failed (table may not exist yet):', error.message);
        return;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user_tour_state', tourId] }),
  });

  return {
    ...query,
    markSeen: () => upsert.mutate('seen'),
    markDismissed: () => upsert.mutate('dismissed'),
    markCompleted: () => upsert.mutate('completed'),
  };
}
